import { useState, useEffect, FC, ChangeEvent, useMemo } from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  ScrollBar,
} from "@ht/components";
import ChatList from "./ChatList";
import { IActiveRoom, IIncomingRoom } from "../../../app/chatAgent/types";
import { TInput } from "@ht/components/src/forms/form-elements/types";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SearchBox } from "../../../components/search-box";
import { useHttpRequest } from "../../../hooks/useHttpRequest";
import { useFetchAgentRoom } from "../hooks/useFetchAgentRoom";
import BroadcastChatIcon from "@ht/shared/fonts/deos-pis/megaphone";
import StartChatIcon from "@ht/shared/fonts/deos-pis/chat-plus";
import ButtonFilter from "../../../components/button-filter";
import BroadcastModal from "./BroadcastModal";
import UserListSlideModal from "./UserListSlideModal";
import { clearChatCallBack } from "../../../app/chat/chatSlice";
import CircularProgress from "../../../components/circular-progress";
import * as toast from "../../../components/toast";
import { activeChatAgentRoom, setFloatingRoomId } from "../../../app/chatAgent/chatAgentSlice";
import Dots from "@ht/shared/fonts/deos-pis/dots";
import DropdownMessageControlItem from "../../../components/dropdown/message/item";
import { setLeftPanelLeads } from "../../../app/layout/layoutStateSlice";

interface IProps {
  className?: string;
}
const SideBar: FC<IProps> = ({ className }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const {
    incomingRoom,
    status: chatCallback,
    activeRoom,
    hideHeader,
    layoutType,
  } = useAppSelector((state) => state.chatagent);
  const [isScroll] = useState(false);
  const [scrollInitialView] = useState<"top" | "bottom">("top");
  const [filteredRooms, setFilteredRooms] = useState<IIncomingRoom[]>([]);
  const [filter, setFilter] = useState<Array<any>>([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalBroadcast, setShowModalBroadcast] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState<Array<any>>([]);
  const [broadcastCount, setBroadcastCount] = useState<number>(0);

  const getSortedRoom = (rooms: IIncomingRoom[]) => {
    return [...rooms].sort(
      (a, b) =>
        new Date(b.latestMessageTime).getTime() -
        new Date(a.latestMessageTime).getTime()
    );
  };
  const { retrieveData, chatBaseUrl, chatRoomUrl } = useFetchAgentRoom(
    String(user?.username)
  );
  const { sendRequest } = useHttpRequest();
  const refreshRoom = async () => {
    const response = await sendRequest({
      baseUrl: chatBaseUrl,
      url: chatRoomUrl,
      method: "get",
    });
    retrieveData(response);
  };
  const loadRoles = async () => {
    const response = await sendRequest({
      baseUrl: chatBaseUrl,
      url: `/Role/${user?.tenantId}`,
      method: "get",
    });
    setRole(response?.data ?? []);
  };

  useEffect(() => {
    if (user?.tenantId) {
      loadRoles().catch((e) => console.log("error load role", e.message));
    }
  }, [user]);

  const filterData = useMemo(() => {
    const filterRole = role.map((item) => ({
      key: {
        label: "Role",
        value: "role_id",
      },
      data: { label: item.name, value: item.id },
    }));
    const result = [...filterRole];

    return result;
  }, [role]);

  const search = (array: IIncomingRoom[], keyword: string) => {
    const filtered = array?.filter(
      (a) =>
        a.fullName?.toLowerCase().includes(keyword) ||
        a.latestMessage?.toLowerCase().includes(keyword)
    );
    const sortedRooms = getSortedRoom(filtered);
    let filterByRole = sortedRooms;
    if (filter.length > 0)
      filterByRole = sortedRooms.filter((a) =>
        filter[0].values.includes(a.roleId)
      );
    if (filtered)
      setFilteredRooms(
        filterByRole.filter((a) => a.participant.includes(user?.username ?? ""))
      );
  };
  const searchHandler = (e: ChangeEvent<TInput>) => {
    const value = e.target.value.toLowerCase();
    search(incomingRoom, value);
  };
  const handleFilter = (result: any[]) => {
    setFilter(result);
  };
  const handleClearFilter = () => {
    setFilter([]);
  };
  const handleStartChat = async (roomId: any) => {
    await refreshRoom();
    const obj = incomingRoom.find((a) => a.roomId == roomId);
    const activeChatObj = {
      userName: obj?.userName,
      roomId,
      sessionId: obj?.sessionId,
    };
    localStorage.setItem("activeChatRoom", JSON.stringify(activeChatObj));
    if (layoutType == "PAGE") {
      navigate(`/chat/${roomId}`)
    }
    if (layoutType == "FLOATING_PANEL") {
        dispatch(setFloatingRoomId(roomId));
    };
  };

  const handleStart = () => {
    if (layoutType == "PAGE") navigate("/chat");
    dispatch(activeChatAgentRoom({} as IActiveRoom));
    setShowModal(true);
  };

  const handleSend = (bcCount: number) => {
    setBroadcastCount(bcCount);
  };

  const endProgress = () => {
    setLoading(false);
    setProgress(0);
    toast.success(
      `Your broadcast message has been sent to ${broadcastCount} user(s)`
    );
    dispatch(clearChatCallBack());
  };

  useEffect(() => {
    if (currentProgress) {
      console.log("progress", currentProgress);
      setProgress(currentProgress / broadcastCount);
    }
  }, [currentProgress, broadcastCount, chatCallback?.progress]);

  useEffect(() => {
    if (progress >= 1) {
      const timer = setTimeout(() => {
        endProgress();
      }, 1000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [progress]);

  useEffect(() => {
    if (
      chatCallback.status == "STARTNEWCHAT" &&
      !chatCallback.isFromBroadcast
    ) {
      dispatch(activeChatAgentRoom({} as IActiveRoom));
      handleStartChat(chatCallback.roomId).catch((e) => console.log(e));
    } else if (chatCallback.status == "INCOMINGROOM") {
      refreshRoom().catch((e) => console.log("error refresh room", e.message));
    } else if (
      chatCallback.status == "RESOLVED" &&
      activeRoom.sessionId == chatCallback.sessionId
    ) {
      if (layoutType == "PAGE") navigate("/chat");
      dispatch(activeChatAgentRoom({} as IActiveRoom));
    } else if (
      chatCallback.status == "PROGRESS" &&
      chatCallback.message == "BEGIN"
    ) {
      setLoading(true);
      setProgress(0);
      setCurrentProgress(0);
    } else if (chatCallback.status == "BROADCAST") {
      setCurrentProgress((state) => state + 1);
    } else if (
      chatCallback.status == "PROGRESS" &&
      chatCallback.message == "END"
    ) {
      const timer = setTimeout(() => {
        endProgress();
      }, 1000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [chatCallback, layoutType]);

  useEffect(() => {
    if (incomingRoom) {
      const sortedRooms = getSortedRoom(incomingRoom);
      let filterByRole = sortedRooms;
      if (filter.length > 0)
        filterByRole = sortedRooms.filter((a) =>
          filter[0].values.includes(a.roleId)
        );
      setFilteredRooms(
        filterByRole.filter((a) => a.participant.includes(user?.username ?? ""))
      );
    }
  }, [incomingRoom, filter]);

  const getLeftPanelOption = () => {
    const callScheduleHandler = () => {
      dispatch(setLeftPanelLeads("CALL_SCHEDULE"));
    };
    const profileHandler = () => {
      dispatch(setLeftPanelLeads("PROFILE"));
    };
    const chatHandler = () => {
      dispatch(setLeftPanelLeads("CHAT_AGENT"));
    };
    return (
      <div className="sm:flex">
        <Dropdown className="block" direction="down" showIcon={false} shape>
          <DropdownToggle
            variant="texted"
            iconButton
            size="lg"
            shape="ellipse"
            customStyle="only-icon"
            className="text-[26px] w-[32px] h-[32px] relative content-start"
          >
            <Dots className={clsx("w-6 h-6 text-gray-400")} />
          </DropdownToggle>
          <DropdownMenu
            className={clsx(
              "rounded p-0 !w-[9rem] mt-[1.125rem] -mr-[1.2rem] border-t-0 right-0 left-auto"
            )}
          >
            <div className="dropdown-list rounded-b">
              <DropdownMessageControlItem
                onClick={callScheduleHandler}
                additionalClass="rounded-b"
                text="Call Schedule"
              />
            </div>
            <div className="dropdown-list rounded-b">
              <DropdownMessageControlItem
                onClick={profileHandler}
                additionalClass="rounded-b"
                text="Customer Profile"
              />
            </div>
            <div className="dropdown-list rounded-b">
              <DropdownMessageControlItem
                onClick={chatHandler}
                additionalClass="rounded-b"
                text="Chat"
              />
            </div>
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  };
  return (
    <>
      <toast.ToastProvider />
      <div className={clsx("bg-gray-100 w-80 ", className)}>
        <CircularProgress
          loading={loading}
          position="FULL_WINDOW"
          progress={progress}
          setProgress={setProgress}
        />
        <div
          className={clsx(
            "sidebar bg-gray-100 rounded-md h-[calc(100vh_-_70px)] lg:h-[calc(100vh_-_70px)] maxLg:absolute maxLg:w-full "
          )}
        >
          <div className="px-4">
            {!hideHeader && (
              <div className="xxsToXs: mt-4 mb-5 flex flex-row justify-between items-center">
                <div className="text-lg text-gray-700 font-bold"> Chats</div>
                <div className="flex gap-4">
                  <button onClick={() => setShowModalBroadcast(true)}>
                    <BroadcastChatIcon className="cursor-pointer h-5 w-5 text-gray-400" />
                  </button>
                  <button onClick={() => handleStart()}>
                    <StartChatIcon className="cursor-pointer h-5 w-5 text-gray-400" />
                  </button>
                  {layoutType == "LEFT_PANEL" && getLeftPanelOption()}
                </div>
              </div>
            )}
            <div className="mb-3 flex gap-3 items-center justify-between">
              <div className="flex-1">
                <SearchBox
                  name="searchRoom"
                  className="bg-gray-200 focus:bg-gray-200"
                  onChange={searchHandler}
                />
              </div>
              <ButtonFilter
                selected={filter}
                data={filterData}
                onFilter={handleFilter}
                onClear={handleClearFilter}
                showIconOnly
                className="border-none px-2"
              />
            </div>
          </div>

          <ScrollBar
            bottom="100px"
            className="rounded-lg"
            initialView={scrollInitialView}
            isPositionChange={isScroll}
          >
            {filteredRooms ? (
              <ChatList items={filteredRooms} />
            ) : (
              <div>Loading...</div>
            )}
          </ScrollBar>
        </div>

        <UserListSlideModal
          onClose={() => setShowModal(false)}
          visible={showModal}
        />

        <BroadcastModal
          onClose={() => setShowModalBroadcast(false)}
          visible={showModalBroadcast}
          onSend={handleSend}
        />
      </div>
    </>
  );
};

export default SideBar;
